// src/modules/calendar/hooks/useCalendarNotifications.js
import { useEffect, useRef, useCallback } from 'react';
import { db } from '../../../services/firebase';
import { collection, query, where, doc, getDoc, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { getCalendarWorkspaceId } from '../services';
import toast from 'react-hot-toast';

// Interval solo para chequear alertas con datos ya en cache (NO hace queries a Firestore)
const CHECK_INTERVAL = 30000;

const DEFAULT_SETTINGS = {
    enabled: true,
    alerts: [30, 10], // minutes before
    sound: true,
    showZoomLink: true,
};

const toLocalDateStr = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const to12h = (d) => {
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return m > 0 ? `${h12}:${String(m).padStart(2, '0')}${ampm}` : `${h12}${ampm}`;
};

const playSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const playTone = (freq, start, dur) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.15, ctx.currentTime + start);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
            osc.start(ctx.currentTime + start);
            osc.stop(ctx.currentTime + start + dur);
        };
        playTone(523, 0, 0.15);
        playTone(659, 0.15, 0.15);
        playTone(784, 0.3, 0.2);
    } catch { }
};

const showDesktopNotif = (title, body, icon = '/favicon.ico') => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.ready.then(reg => {
                reg.showNotification(title, {
                    body, icon, badge: '/favicon.ico',
                    tag: `cal-${Date.now()}`,
                    vibrate: [200, 100, 200],
                    requireInteraction: true,
                    data: { url: '/calendar' }
                });
            });
        } else {
            const n = new Notification(title, { body, icon, tag: `cal-${Date.now()}` });
            n.onclick = () => { window.focus(); n.close(); };
        }
    } catch { }
};

const useCalendarNotifications = () => {
    const { user } = useAuth();
    const { addNotification } = useNotifications();
    const notifiedRef = useRef(new Set());
    const settingsRef = useRef(null);
    const eventsCache = useRef([]);
    // Cache for instructor/course info to avoid repeated getDoc calls
    const infoCache = useRef({});

    const loadSettings = useCallback(async () => {
        if (!user?.uid) return DEFAULT_SETTINGS;
        try {
            const snap = await getDoc(doc(db, 'users', user.uid));
            const data = snap.data();
            const calNotifs = data?.calendarNotifications;
            if (calNotifs && typeof calNotifs.enabled !== 'undefined') {
                return calNotifs;
            }
        } catch { }
        return DEFAULT_SETTINGS;
    }, [user?.uid]);

    // Check cached events against alert times (NO Firestore reads)
    const checkAlerts = useCallback(async () => {
        const settings = settingsRef.current;
        if (!settings?.enabled) return;

        const now = new Date();
        const today = toLocalDateStr(now);

        const todayEvents = eventsCache.current.filter(ev => toLocalDateStr(ev.start) === today);

        for (const ev of todayEvents) {
            const diffMs = ev.start.getTime() - now.getTime();
            const diffMin = Math.floor(diffMs / 60000);

            for (const alertMin of (settings.alerts || [30, 10])) {
                const key = `${ev.id}-${alertMin}`;
                if (notifiedRef.current.has(key)) continue;

                if (diffMin <= alertMin && diffMin > 0) {
                    notifiedRef.current.add(key);

                    const timeStr = to12h(ev.start);
                    const type = ev.type === 'masterclass' ? 'MC' : 'Clase';
                    const title = diffMin <= 5
                        ? `En ${diffMin} min: ${type}`
                        : `En ${alertMin} min: ${type}`;
                    const courseName = ev.title || 'Evento';

                    let body = `${courseName} - ${timeStr}`;

                    // Get instructor name (cached)
                    if (ev.instructorId) {
                        const cacheKey = `inst-${ev.instructorId}`;
                        if (!infoCache.current[cacheKey]) {
                            try {
                                const instSnap = await getDoc(doc(db, 'instructors', ev.instructorId));
                                infoCache.current[cacheKey] = instSnap.exists() ? instSnap.data().name : null;
                            } catch { infoCache.current[cacheKey] = null; }
                        }
                        if (infoCache.current[cacheKey]) {
                            body += ` con ${infoCache.current[cacheKey]}`;
                        }
                    }

                    // Get zoom link if configured (cached).
                    // El link propio de ESTE evento manda siempre — es el que quedó
                    // asignado al distribuir ese día. Solo se recurre al link
                    // "por defecto" del curso (compartido entre todos sus días) si
                    // este evento en particular todavía no tiene uno propio.
                    if (settings.showZoomLink && ev.type === 'masterclass') {
                        let zoomLink = ev.mcLiveUrl || null;
                        if (!zoomLink && ev.courseId) {
                            const cacheKey = `course-${ev.courseId}`;
                            if (!infoCache.current[cacheKey]) {
                                try {
                                    const courseSnap = await getDoc(doc(db, 'courses', ev.courseId));
                                    infoCache.current[cacheKey] = courseSnap.exists() ? courseSnap.data().defaultVariables?.zoomLink : null;
                                } catch { infoCache.current[cacheKey] = null; }
                            }
                            zoomLink = infoCache.current[cacheKey];
                        }
                        if (zoomLink) {
                            body += `\nZoom: ${zoomLink}`;
                        }
                    }

                    // Show toast
                    toast(
                        (t) => (
                            <div onClick={() => toast.dismiss(t.id)} style={{ cursor: 'pointer' }}>
                                <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '2px' }}>
                                    {diffMin <= 5 ? '🔴' : '⏰'} {title}
                                </div>
                                <div style={{ fontSize: '12px', color: '#4b5563', whiteSpace: 'pre-line' }}>{body}</div>
                            </div>
                        ),
                        {
                            duration: diffMin <= 5 ? 30000 : 10000,
                            position: 'top-right',
                            style: {
                                background: '#fff',
                                border: '1px solid #e5e7eb',
                                borderLeft: `4px solid ${diffMin <= 5 ? '#ef4444' : '#f59e0b'}`,
                                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                                padding: '12px 16px',
                                maxWidth: '400px',
                            },
                        }
                    );

                    showDesktopNotif(title, body.replace('\n', ' - '));
                    if (settings.sound) playSound();

                    addNotification({
                        type: 'calendar_reminder',
                        title,
                        body: body.replace('\n', ' - '),
                        eventId: ev.id,
                        courseId: ev.courseId || null,
                    });
                }
            }

            // Already started notification
            if (diffMin <= 0 && diffMin > -2) {
                const startKey = `${ev.id}-started`;
                if (!notifiedRef.current.has(startKey)) {
                    notifiedRef.current.add(startKey);
                    const type = ev.type === 'masterclass' ? 'MC' : 'Clase';
                    toast(`🔴 Iniciando: ${type} ${ev.title || ''} - ${to12h(ev.start)}`, {
                        duration: 15000,
                        icon: '🎯',
                        style: { borderLeft: '4px solid #ef4444', fontWeight: 600 },
                    });
                }
            }
        }
    }, [addNotification]);

    useEffect(() => {
        if (!user?.uid) return;

        let unsubscribe = null;
        let intervalId = null;
        let midnightTimer = null;
        let active = true;

        const setup = async () => {
            if (!active) return;

            if ('Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission();
            }

            const settings = await loadSettings();
            settingsRef.current = settings;

            if (!settings.enabled) {
                console.log('[CalNotif] Disabled');
                return;
            }

            console.log('[CalNotif] Active - alerts at', settings.alerts, 'min before');

            // onSnapshot en vez de getDocs polling: solo lee cuando cambian los datos
            const wsId = getCalendarWorkspaceId();
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

            const constraints = [];
            if (wsId) constraints.push(where('workspaceId', '==', wsId));
            constraints.push(
                where('status', '==', 'confirmed'),
                where('start', '>=', Timestamp.fromDate(startOfDay)),
                where('start', '<', Timestamp.fromDate(endOfDay)),
                orderBy('start', 'asc')
            );

            unsubscribe = onSnapshot(
                query(collection(db, 'schedules'), ...constraints),
                (snapshot) => {
                    eventsCache.current = snapshot.docs.map(d => {
                        const data = d.data();
                        return {
                            id: d.id,
                            ...data,
                            start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                            end: data.end?.toDate ? data.end.toDate() : new Date(data.end)
                        };
                    });
                    console.log(`[CalNotif] ${eventsCache.current.length} eventos hoy (listener)`);
                    checkAlerts().catch(() => {});
                },
                (error) => {
                    console.error('[CalNotif] Listener error:', error);
                }
            );

            // Interval solo chequea alertas con datos ya en cache (0 lecturas Firestore)
            intervalId = setInterval(() => { checkAlerts().catch(() => {}); }, CHECK_INTERVAL);

            // Reset a medianoche: re-suscribir con el rango del nuevo día
            const msToMidnight = endOfDay.getTime() - Date.now();
            midnightTimer = setTimeout(() => {
                if (unsubscribe) unsubscribe();
                if (intervalId) clearInterval(intervalId);
                notifiedRef.current.clear();
                infoCache.current = {};
                eventsCache.current = [];
                setup();
            }, msToMidnight + 1000);
        };

        setup();

        return () => {
            active = false;
            if (unsubscribe) unsubscribe();
            if (intervalId) clearInterval(intervalId);
            if (midnightTimer) clearTimeout(midnightTimer);
        };
    }, [user?.uid, loadSettings, checkAlerts]);
};

export default useCalendarNotifications;
